import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';

import * as Styled from './tooltip.style';
import { Positions } from './types';

const positions = Object.values(Positions);

const getPositionsList = (position: Positions) => {
  const index = positions.indexOf(position);
  return index === -1
    ? positions
    : [...positions.slice(index + 1), ...positions.slice(0, index)];
};

export type TooltipProps = {
  label: string | React.ReactNode;
  position?: Positions;
  referenceElement?: React.RefObject<HTMLElement>;
  location?: { top: number; left: number };
  isVisible?: boolean;
  delay?: number;
};

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      label,
      position = Positions.Top,
      referenceElement,
      location,
      isVisible,
      delay,
    },
    ref,
  ) => {
    const containerRef = useForwardedRef(ref);
    const [internalVisible, setInternalVisible] = useState(false);
    const visible = isVisible ?? internalVisible;

    const fallbackPositions = useMemo(
      () => getPositionsList(position),
      [position],
    );

    const { refs, floatingStyles, placement, isPositioned } = useFloating({
      elements: {
        reference: referenceElement?.current,
      },
      placement: position,
      whileElementsMounted: visible
        ? (reference, floating, update) =>
            autoUpdate(reference, floating, update, {
              animationFrame: true,
            })
        : undefined,
      strategy: 'fixed',
      middleware: [
        offset({ mainAxis: 8, alignmentAxis: 8 }),
        flip({
          fallbackPlacements: fallbackPositions,
        }),
        shift({ padding: Styled.positioningOffset }),
      ],
    });

    useEffect(() => {
      refs.setFloating(containerRef.current);
    }, [containerRef, refs]);

    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
      if (referenceElement?.current) {
        referenceElement.current.onmouseenter = () => {
          // in case of referenceElement movement or delays, this makes sure it is set correctly
          refs.setPositionReference(referenceElement?.current);

          if (delay) {
            hoverTimeoutRef.current = setTimeout(() => {
              setInternalVisible(true);
              hoverTimeoutRef.current = null;
            }, delay);
            return;
          }

          setInternalVisible(true);
        };

        referenceElement.current.onmouseleave = () => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setInternalVisible(false);
        };
      }

      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
      };
    }, [containerRef, referenceElement, refs, delay]);

    useEffect(() => {
      if (location) {
        setInternalVisible(true);
        refs.setPositionReference({
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            x: location.left,
            y: location.top,
            top: location.top,
            right: location.left,
            bottom: location.top,
            left: location.left,
          }),
        });
      }
    }, [location, refs]);

    return (
      <Styled.Container
        ref={containerRef}
        style={floatingStyles}
        $placement={placement}
        $isVisible={visible && isPositioned}
      >
        <Styled.Label>{label}</Styled.Label>
      </Styled.Container>
    );
  },
);
